// Shared foreground/background location-watching helper. Native app
// builds (Capacitor) use @capacitor-community/background-geolocation,
// which keeps reporting location while the phone is locked or the app is
// backgrounded (via a persistent tracking notification on Android, and
// the "Always Allow" location permission on both platforms -- see
// ios/App/App/Info.plist and PHASE0_DECISIONS.md's P2-4). The plain web
// build (browser tab / installed PWA) falls back to
// navigator.geolocation.watchPosition, which only reports while the page
// is in the foreground -- there is no browser API for true background
// tracking outside of a native shell.
import { Capacitor, registerPlugin } from '@capacitor/core';

const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

export const isNativePlatform = () => Capacitor.isNativePlatform();

// Starts watching location and calls onLocation(lat, lng) on every update.
// Returns an async stop() function. On native, this is also the call that
// triggers the OS location-permission prompt (requestPermissions: true) --
// callers should only invoke this in direct response to the driver opting
// in via an in-app explanation, not automatically on mount, since both
// Apple and Google expect a primed explanation before the system's
// "Always Allow" dialog for background location.
export async function startLocationWatch({ onLocation, onError }) {
  if (isNativePlatform()) {
    const watcherId = await BackgroundGeolocation.addWatcher(
      {
        backgroundTitle: 'Pro Haul — Trip in progress',
        backgroundMessage: 'Sharing your location with dispatch while this load is in transit. Tap to stop.',
        requestPermissions: true,
        stale: false,
        distanceFilter: 50, // meters between updates -- battery/data friendly for highway driving
      },
      (location, error) => {
        if (error) {
          onError?.(error);
          return;
        }
        if (location) onLocation(location.latitude, location.longitude);
      }
    );
    return async () => {
      await BackgroundGeolocation.removeWatcher({ id: watcherId }).catch(() => {});
    };
  }

  if (!navigator.geolocation) return async () => {};
  const watchId = navigator.geolocation.watchPosition(
    (pos) => onLocation(pos.coords.latitude, pos.coords.longitude),
    (err) => onError?.(err),
    { enableHighAccuracy: false, maximumAge: 30000 }
  );
  return async () => navigator.geolocation.clearWatch(watchId);
}

// Deep-links to this app's OS settings page -- surfaced when a driver
// denies the location prompt and needs to grant it manually afterward.
export function openLocationSettings() {
  if (isNativePlatform()) BackgroundGeolocation.openSettings();
}
